import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { BackgroundWrapper, PageContainer, PageTransition } from './';

// Access codes
const MANUAL_ACCESS_CODE = 'EC0409'; // Code printed in the game box for manual entry
const QR_ACCESS_CODES = ['CONECTADOS-V1.0']; // Codes that can be used via QR/URL parameter
const ACCESS_KEY = 'conectados_access';

// Helper function to validate access codes
const isValidAccessCode = (code, isFromUrl = false) => {
  const trimmedCode = code.trim().toUpperCase();
  
  if (isFromUrl) {
    // From URL parameter (QR code) - check QR codes
    return QR_ACCESS_CODES.some(validCode => validCode.toUpperCase() === trimmedCode);
  } else {
    // From manual input - check manual code
    return trimmedCode === MANUAL_ACCESS_CODE.toUpperCase();
  }
};

const AccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem;
`;

const AccessCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
  text-align: center;

  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1rem;
  }
`;

const Title = styled.h1`
  color: #234e9d;
  font-size: 2rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Message = styled.p`
  color: #555;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  margin-bottom: 1rem;
  transition: border-color 0.3s ease;
  text-align: center;
  font-weight: 500;
  letter-spacing: 1px;

  &:focus {
    outline: none;
    border-color: #234e9d;
  }

  &::placeholder {
    color: #999;
    letter-spacing: normal;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #234e9d, #1a3a75);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(35, 78, 157, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #d32f2f;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

const SuccessMessage = styled.p`
  color: #388e3c;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

const InfoBox = styled.div`
  background: rgba(35, 78, 157, 0.1);
  border-left: 4px solid #234e9d;
  padding: 1rem;
  margin-top: 2rem;
  border-radius: 5px;
  text-align: left;
`;

const InfoText = styled.p`
  color: #234e9d;
  font-size: 0.9rem;
  margin: 0;
  line-height: 1.5;
`;

function AccessControl({ children, onAccessGranted }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasAccess, setHasAccess] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const t = useTranslation();

  useEffect(() => {
    // Add meta tag to prevent search engine indexing
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex,nofollow';
    document.head.appendChild(metaRobots);

    // Check for access code in URL parameter
    const urlAccessCode = searchParams.get('access');
    
    // Check for stored access in localStorage
    const storedAccess = localStorage.getItem(ACCESS_KEY);

    if (urlAccessCode) {
      // Validate QR/URL access code
      if (isValidAccessCode(urlAccessCode, true)) {
        // Store the access in localStorage (we just store a flag, not the code itself)
        localStorage.setItem(ACCESS_KEY, 'granted');
        setHasAccess(true);
        setSuccess(t('conectados.access.success'));
        
        // Clean the URL by removing the access parameter
        // This prevents the code from being visible in the address bar or browser history
        navigate(location.pathname, { replace: true });
        
        if (onAccessGranted) {
          onAccessGranted();
        }
      } else {
        setError(t('conectados.access.invalidCode'));
      }
      setIsChecking(false);
    } else if (storedAccess === 'granted') {
      // User has previously entered a valid code
      setHasAccess(true);
      setIsChecking(false);
      if (onAccessGranted) {
        onAccessGranted();
      }
    } else {
      setIsChecking(false);
    }

    // Cleanup: remove the meta tag when component unmounts
    return () => {
      const existingMeta = document.querySelector('meta[name="robots"]');
      if (existingMeta) {
        existingMeta.remove();
      }
    };
  }, [searchParams, navigate, location.pathname, t, onAccessGranted]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!accessCode.trim()) {
      setError(t('conectados.access.emptyCode'));
      return;
    }

    // Validate manual access code
    if (isValidAccessCode(accessCode, false)) {
      // Store the access in localStorage (we just store a flag, not the code itself)
      localStorage.setItem(ACCESS_KEY, 'granted');
      setHasAccess(true);
      setSuccess(t('conectados.access.success'));
      if (onAccessGranted) {
        onAccessGranted();
      }
    } else {
      setError(t('conectados.access.invalidCode'));
    }
  };

  const handleInputChange = (e) => {
    setAccessCode(e.target.value);
    setError('');
    setSuccess('');
  };

  // If checking, show loading
  if (isChecking) {
    return (
      <BackgroundWrapper>
        <PageContainer>
          <AccessContainer>
            <AccessCard>
              <Title>{t('common.loading')}</Title>
            </AccessCard>
          </AccessContainer>
        </PageContainer>
      </BackgroundWrapper>
    );
  }

  // If user has access, render the children (game content)
  if (hasAccess) {
    return children;
  }

  // Otherwise, show the access control form
  return (
    <BackgroundWrapper>
      <PageContainer>
        <PageTransition>
          <AccessContainer>
            <AccessCard>
              <Title>{t('conectados.access.title')}</Title>
              <Message>{t('conectados.access.message')}</Message>
              
              <form onSubmit={handleSubmit}>
                <Input
                  type="text"
                  value={accessCode}
                  onChange={handleInputChange}
                  placeholder={t('conectados.access.placeholder')}
                  autoFocus
                />
                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}
                <Button type="submit" disabled={!accessCode.trim()}>
                  {t('conectados.access.submit')}
                </Button>
              </form>

              <InfoBox>
                <InfoText>
                  <strong>{t('conectados.access.infoTitle')}</strong>
                </InfoText>
                <InfoText>{t('conectados.access.infoText')}</InfoText>
              </InfoBox>
            </AccessCard>
          </AccessContainer>
        </PageTransition>
      </PageContainer>
    </BackgroundWrapper>
  );
}

export default AccessControl;

